import React, { cloneElement, useRef, useState, useCallback, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import { FlexRow, FlexRowBetween } from './Common'
import styled from 'styled-components'
import { Form, Button, Icon, Label, Modal } from 'semantic-ui-react'
import { ArrowClockwise, FilePdf, X } from 'phosphor-react'
import { CommonUtility, MediaUtility, base64ToBlobUrl } from 'utility'
import Webcam from 'react-webcam'

export const ImageEnlargeModal = ({
    open,
    onClose,
    src,
    alt = 'Preview',
    rotation = 0,
}) => {
    const quarterTurn = Number(rotation) % 180 !== 0
    return (
        <Modal
            open={Boolean(open && src)}
            onClose={onClose}
            size="large"
            className="model"
        >
            <Modal.Header>
                <FlexRowBetween>
                    <span>Image preview</span>
                    <X
                        className="pointer"
                        size={28}
                        onClick={onClose}
                    />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                {src ? (
                    <img
                        src={src}
                        alt={alt}
                        style={{
                            width: '100%',
                            maxWidth: quarterTurn ? '70vh' : '100%',
                            maxHeight: quarterTurn ? '100%' : '70vh',
                            objectFit: 'contain',
                            borderRadius: 8,
                            display: 'block',
                            margin: '0 auto',
                            transform: rotation
                                ? `rotate(${rotation}deg)`
                                : undefined,
                            transformOrigin: 'center center',
                        }}
                    />
                ) : null}
            </Modal.Content>
            <Modal.Actions>
                <Button type="button" onClick={onClose}>
                    Close
                </Button>
            </Modal.Actions>
        </Modal>
    )
}

const PreviewImageWrap = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 80px;
    background: #ececec;
    border-radius: 8px;
    overflow: hidden;

    .preview-loading {
        position: absolute;
        inset: 0;
        background: linear-gradient(
            90deg,
            #ececec 25%,
            #e0e0e0 50%,
            #ececec 75%
        );
        background-size: 200% 100%;
        animation: preview-shimmer 1.2s infinite;
    }

    @keyframes preview-shimmer {
        0% {
            background-position: 200% 0;
        }
        100% {
            background-position: -200% 0;
        }
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 8px;
        transition: opacity 0.2s ease;
        pointer-events: none;
    }
`

const ImgPreview = styled.div`
    border: solid 2px #d6d6d7;
    border-radius: 10px;
    position: relative;
    cursor: ${props => (props.$clickable ? 'zoom-in' : 'default')};
    .preview {
        svg {
            width: 100% !important;
            height: 100% !important;
        }
    }
    .delete-icon {
        position: absolute;
        top: 5px;
        right: 5px;
        background: rgba(0, 0, 0, 0.7);
        border-radius: 50%;
        padding: 4px;
        cursor: pointer;
        z-index: 100;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        min-width: 26px;
        min-height: 26px;
        &:hover {
            background: rgba(220, 53, 69, 0.9);
        }
    }
    .rotate-icon {
        position: absolute;
        top: 5px;
        left: 5px;
        width: 22px;
        height: 22px;
        min-width: 22px;
        min-height: 22px;
        max-width: 22px;
        max-height: 22px;
        padding: 0;
        box-sizing: border-box;
        background: rgba(0, 0, 0, 0.7);
        border-radius: 50%;
        cursor: pointer;
        z-index: 100;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 0;
        transition: background 0.2s;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        &:hover {
            background: rgba(0, 123, 255, 0.9);
        }
        svg,
        i.icon {
            display: block;
            margin: 0 !important;
            width: 14px !important;
            height: 14px !important;
            font-size: 14px !important;
            line-height: 1 !important;
            flex: 0 0 14px;
        }
    }
`

const PreviewContainer = styled(FlexRow)`
    gap: 10px;
    justify-content: unset;
    .img {
        height: 200px;
    }
    img {
        width: 100%;
        height: 100%;
        border-radius: 10px;
    }
`

export const SinglePreview = ({
    tagPreview = false,
    item,
    onRemove,
    onRotate,
    className,
    fullview = false,
    remove = false,
    disableOpen = false,
    onPreviewClick,
    onDeleteHover,
}) => {
    const previewUrl = MediaUtility.resolvePreviewUrl(item) || item?.url || ''
    const previewItem = { ...item, url: previewUrl }
    const isImage = MediaUtility.checkIsImage(previewItem)
    const isPdf = MediaUtility.checkIsPDF(previewItem)
    const [imageLoaded, setImageLoaded] = useState(false)
    const [enlargeOpen, setEnlargeOpen] = useState(false)
    const canOpenPreview = Boolean(previewUrl) && (!disableOpen || Boolean(onPreviewClick))

    useEffect(() => {
        setImageLoaded(false)
    }, [previewUrl])

    const openFile = data => {
        if (typeof onPreviewClick === 'function') {
            onPreviewClick(data)
            return
        }
        const url = MediaUtility.resolvePreviewUrl(data) || data?.url
        if (disableOpen || !url) {
            return
        }
        if (isImage) {
            setEnlargeOpen(true)
            return
        }
        window.open(url, '_blank')
    }

    if (!isImage && !isPdf) {
        return null
    }

    return (
        <>
            <>
                {tagPreview ? (
                    <Label
                        title={item?.file?.name || item.FileKey}
                        className="d-flex align-items-center pointer"
                    >
                        <span
                            title={item?.file?.name || item?.FileKey}
                            onClick={() => openFile(item)}
                        >
                            {CommonUtility.truncateString(
                                item?.file?.name || item?.FileKey,
                                20,
                            )}
                        </span>
                        {remove && (
                            <X
                                size={15}
                                onClick={() => {
                                    if (onRemove) {
                                        onRemove(item)
                                    }
                                }}
                            />
                        )}
                    </Label>
                ) : (
                    <ImgPreview
                        onClick={() => openFile(previewItem)}
                        title={
                            canOpenPreview
                                ? item?.comment || 'Click to enlarge'
                                : item?.comment
                        }
                        $clickable={canOpenPreview && isImage}
                        className={`${
                            fullview ? 'col-12' : 'col-4'
                        } img ${className}`}
                    >
                        {isImage && previewUrl && (
                            <PreviewImageWrap>
                                {!imageLoaded && (
                                    <div className="preview-loading" />
                                )}
                                <img
                                    src={previewUrl}
                                    alt="preview"
                                    onLoad={() => setImageLoaded(true)}
                                    style={{
                                        opacity: imageLoaded ? 1 : 0,
                                        transform: item?.viewRotation
                                            ? `rotate(${item.viewRotation}deg)`
                                            : undefined,
                                        transformOrigin: 'center center',
                                    }}
                                />
                            </PreviewImageWrap>
                        )}
                        {isPdf && previewUrl && (
                            <div className="preview">
                                <FilePdf />
                            </div>
                        )}
                        {onRotate && isImage && (
                            <div
                                className="rotate-icon"
                                role="button"
                                tabIndex={0}
                                onMouseDown={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                }}
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    onRotate(item)
                                }}
                                title="Rotate image"
                            >
                                <ArrowClockwise
                                    size={14}
                                    color="white"
                                    weight="bold"
                                />
                            </div>
                        )}
                        {remove && (
                            <div
                                className="delete-icon"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    if (onRemove) {
                                        onRemove(item)
                                    }
                                }}
                                onMouseEnter={(e) => {
                                    e.stopPropagation()
                                    if (onDeleteHover) {
                                        onDeleteHover()
                                    }
                                }}
                                title="Delete image"
                            >
                                <X
                                    size={18}
                                    color="white"
                                    weight="bold"
                                />
                            </div>
                        )}
                    </ImgPreview>
                )}
            </>
            <ImageEnlargeModal
                open={enlargeOpen}
                onClose={() => setEnlargeOpen(false)}
                src={previewUrl}
                alt={item?.Caption || item?.FileKey || 'Preview'}
                rotation={item?.viewRotation || 0}
            />
        </>
    )
}

function PreviewList({
    files = [],
    maxFiles,
    error,
    tagPreview = false,
    remove,
    onRemove,
    fullview = false,
    previewItemClassName,
    description = '',
    customePreview,
    previewItem,
}) {
    if ((maxFiles || 0) === 1 && (files || []).length === 0) {
        return (
            <div>
                {error ? <p style={{ color: 'red' }}>{error}</p> : description}
            </div>
        )
    }
    return (
        <PreviewContainer className="flex-wrap flex-row mt-4 justify-content-start">
            {files.map((ele, i) => (
                <React.Fragment key={ele.url + i}>
                    {customePreview ? (
                        <>
                            {cloneElement(previewItem, {
                                item: ele,
                                className: previewItemClassName,
                                remove,
                                onRemove,
                                tagPreview,
                                fullview,
                            })}
                        </>
                    ) : (
                        <SinglePreview
                            item={ele}
                            tagPreview={tagPreview}
                            remove={remove}
                            onRemove={onRemove}
                            className={previewItemClassName}
                            fullview={fullview}
                        />
                    )}
                </React.Fragment>
            ))}
        </PreviewContainer>
    )
}

export const FileUploadPicker = ({
    label,
    customeLabel = false,
    description,
    // minHeight,
    maxFileSize,
    accept,
    maxFiles,
    files = [],
    addedNewFiles,
    tagPreview,
    remove,
    onRemove,
    // maxPreview,
    fullview,
    previewItemClassName,
    noPreview = false,
    customePreview,
    previewItem,
    defaultError,
    onlyPreview,
    showTakePicture = true,
    disabled = false,
    uploadButtonText = "Upload Picture",
    captureButtonText = 'Take Picture',
    uploadButtonColor = 'blue',
    captureButtonColor = 'teal',
    stackMediaButtons = false,
    mediaActionIcons = false,
    dense = false,
    miniActions = false,
    isUploading = false,
}) => {
    const fileInputRef = useRef()
    const [error, setError] = useState('')
    const [internalUploading, setInternalUploading] = useState(false)
    const [uploadSource, setUploadSource] = useState(null)
    const [isCameraOpen, setIsCameraOpen] = useState(false)
    // eslint-disable-next-line no-unused-vars
    const videoRef = useRef()
    const webcamRef = useRef(null)
    // eslint-disable-next-line no-unused-vars
    const [imgSrc, setImgSrc] = useState(null)

    const uploading = Boolean(isUploading) || internalUploading
    const isCaptureUpload = uploadSource === 'capture'
    const uploadButtonLoading =
        !isCaptureUpload &&
        (Boolean(isUploading) ||
            (internalUploading && uploadSource === 'upload'))
    const captureButtonLoading =
        isCaptureUpload && (internalUploading || Boolean(isUploading))

    const invokeAddedNewFiles = async (payload, source = 'upload') => {
        const result = addedNewFiles(payload)
        if (result && typeof result.then === 'function') {
            setUploadSource(source)
            setInternalUploading(true)
            try {
                await result
            } finally {
                setInternalUploading(false)
                setUploadSource(null)
            }
            return
        }
        setInternalUploading(false)
        setUploadSource(null)
    }

    const addNewFiles = async newFiles => {
        if ((maxFiles || 0) === 1 && newFiles.length > 0) {
            const url = URL.createObjectURL(newFiles[0])
            await invokeAddedNewFiles([
                {
                    file: newFiles[0],
                    url,
                    id: new Date().toISOString(),
                    local: true,
                },
            ])
            fileInputRef.current.value = null
            return
        }
        const temp = []
        for (let index = 0; index < newFiles.length; index += 1) {
            const file = newFiles[index]
            if (!maxFileSize || file.size / 1048576 <= maxFileSize) {
                // in MB
                if (!maxFiles || files.length < maxFiles) {
                    temp.push({
                        file: {
                            name: file.name,
                            file,
                        },
                        local: true,
                        url: URL.createObjectURL(file),
                        id: new Date().toISOString() + index,
                    })
                }
            }
        }
        await invokeAddedNewFiles(temp)
        fileInputRef.current.value = null
    }

    const onDrop = async (acceptedFiles, rejectedFiles) => {
        if (maxFiles < rejectedFiles.length) {
            setError(`Only ${maxFiles} files allowed`)
            return
        }
        if ((maxFiles || 0) === 1 && rejectedFiles.length > 0) {
            setError('Invalid File Type')
        } else {
            setError('')
        }
        await addNewFiles(acceptedFiles)
    }

    const { getInputProps } = useDropzone({
        accept,
        maxFiles,
        onDrop,
        disabled: disabled || uploading,
    })

    const handleButtonClick = event => {
        event.preventDefault()
        event.stopPropagation()
        if (disabled || uploading) return
        fileInputRef.current.click()
    }

    const openCamera = () => {
        if (disabled || uploading) return
        setIsCameraOpen(true)
    }

    const captureImage = useCallback(async () => {
        const imageSrc = webcamRef.current?.getScreenshot()
        setImgSrc(imageSrc)
        setIsCameraOpen(false)

        if (!imageSrc) {
            return
        }

        setUploadSource('capture')
        setInternalUploading(true)

        try {
            const res = await fetch(imageSrc)
            const blob = await res.blob()
            const file = new File([blob], `capture-${Date.now()}.jpg`, {
                type: 'image/jpeg',
            })

            const newFile = {
                file: {
                    name: file.name,
                    file,
                },
                url: base64ToBlobUrl(imageSrc),
                id: `capture-${Date.now()}`,
                local: true,
            }

            await invokeAddedNewFiles([newFile], 'capture')
        } catch (err) {
            console.error('Error processing image capture:', err)
            setInternalUploading(false)
            setUploadSource(null)
        }
    }, [webcamRef, files, maxFiles, addedNewFiles])

    // eslint-disable-next-line no-unused-vars
    const [mirrored, setMirrored] = useState(true)

    return (
        <Form.Field className={`${dense ? 'mb-0' : 'mb-3 col-12'}`.trim()}>
            <Form.Field className="mb-0">
                {customeLabel || <label className="">{label}</label>}
                {!onlyPreview && (
                    <>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: stackMediaButtons ? 'column' : 'row',
                                gap: miniActions ? 6 : 8,
                                alignItems:
                                    stackMediaButtons ? 'stretch' : 'center',
                                flexWrap: 'wrap',
                            }}
                        >
                            <input
                                {...getInputProps()}
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                            />
                            <Button
                                type="button"
                                onClick={handleButtonClick}
                                color={uploadButtonColor}
                                loading={uploadButtonLoading}
                                disabled={disabled || uploading}
                                {...(miniActions
                                    ? { size: 'mini', compact: true }
                                    : {})}
                                style={
                                    miniActions ? { padding: '5px 8px', fontSize: 12 } : undefined
                                }
                            >
                                {mediaActionIcons && !uploadButtonLoading && (
                                    <Icon
                                        name="upload"
                                        size="small"
                                        style={{ marginRight: miniActions ? 4 : 8 }}
                                    />
                                )}
                                {uploadButtonText}
                            </Button>
                            {showTakePicture && (
                            <Button
                                type="button"
                                onClick={event => {
                                    event.preventDefault()
                                    event.stopPropagation()
                                    openCamera()
                                }}
                                color={captureButtonColor}
                                loading={captureButtonLoading}
                                disabled={disabled || uploading}
                                {...(miniActions
                                    ? { size: 'mini', compact: true }
                                    : {})}
                                style={
                                    miniActions ? { padding: '5px 8px', fontSize: 12 } : undefined
                                }
                            >
                                {mediaActionIcons && !captureButtonLoading && (
                                    <Icon
                                        name="camera"
                                        size="small"
                                        style={{ marginRight: miniActions ? 4 : 8 }}
                                    />
                                )}
                                {captureButtonText}
                            </Button>
                            )}
                        </div>
                    </>
                )}
            </Form.Field>
            {!noPreview && (
                <PreviewList
                    files={files}
                    maxFiles={maxFiles}
                    error={error || defaultError}
                    tagPreview={tagPreview}
                    remove={remove}
                    onRemove={onRemove}
                    fullview={fullview}
                    previewItemClassName={previewItemClassName}
                    description={description}
                    customePreview={customePreview}
                    previewItem={previewItem}
                />
            )}
            {isCameraOpen && (
                <Modal
                    open={isCameraOpen}
                    onClose={() => setIsCameraOpen(false)}
                >
                    <Modal.Header>Capture Image</Modal.Header>
                    <Modal.Content>
                        <Webcam
                            audio={false}
                            ref={webcamRef}
                            mirrored={mirrored}
                            screenshotFormat="image/jpeg"
                            style={{
                                width: '100%',
                                height: 'auto', // Maintain aspect ratio
                                maxWidth: '100vw', // Prevent exceeding screen width
                            }}
                            className="responsive-webcam"
                        />
                    </Modal.Content>
                    <Modal.Actions>
                        <Button
                            type="button"
                            onClick={() => setIsCameraOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            color="green"
                            loading={captureButtonLoading}
                            disabled={uploading}
                            onClick={captureImage}
                        >
                            Capture
                        </Button>
                    </Modal.Actions>
                </Modal>
            )}
            {/* {imgSrc && (
                <PreviewList
                    files={files}
                    maxFiles={maxFiles}
                    error={error || defaultError}
                    tagPreview={tagPreview}
                    remove={remove}
                    onRemove={onRemove}
                    fullview={fullview}
                    previewItemClassName={previewItemClassName}
                    description={description}
                    customePreview={customePreview}
                    previewItem={previewItem}
                />
            )} */}
        </Form.Field>
    )
}
